import { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { getCustomerInitials } from '@/utils/user.utils';
import type { RootState, AppDispatch } from '@/store';

interface PersonPickerUser {
  id: string;
  name: string;
  email: string;
}

export interface SelectedPerson {
  id: string;
  name: string;
  email: string;
}

export interface CandidatePerson {
  id: string;
  name: string;
  email: string;
  initials: string;
}

interface DraftWithIdFields {
  ownerIds: string[];
  internalParticipantIds: string[];
}

type IdFieldKey = 'ownerIds' | 'internalParticipantIds';

interface PersonPickerConfig<Draft extends DraftWithIdFields> {
  selectDraft: (state: RootState) => Draft;
  selectUsers: (state: RootState) => PersonPickerUser[];
  dispatchSetField: (dispatch: AppDispatch, key: IdFieldKey, value: string[]) => void;
  primaryKey: IdFieldKey;
  exclusionKey: IdFieldKey;
}

export interface PersonPickerReturn {
  state: {
    selected: SelectedPerson[];
    candidates: CandidatePerson[];
    searchValue: string;
  };
  handlers: {
    handleSearchChange: (nextValue: string) => void;
    handleRemove: (personId: string) => void;
    handleAdd: (personId: string) => void;
  };
}

export function usePersonPicker<Draft extends DraftWithIdFields>(
  config: PersonPickerConfig<Draft>
): PersonPickerReturn {
  const { selectDraft, selectUsers, dispatchSetField, primaryKey, exclusionKey } = config;

  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectDraft);
  const users = useAppSelector(selectUsers);

  const [searchValue, setSearchValue] = useState('');

  const primaryIds = draft[primaryKey];
  const exclusionIds = draft[exclusionKey];

  const selected = useMemo<SelectedPerson[]>(
    () =>
      users
        .filter((user) => primaryIds.includes(user.id))
        .map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
        })),
    [primaryIds, users]
  );

  const candidates = useMemo<CandidatePerson[]>(() => {
    const query = searchValue.trim().toLowerCase();
    return users
      .filter((user) => !primaryIds.includes(user.id))
      .filter((user) => !exclusionIds.includes(user.id))
      .filter((user) => {
        if (query.length === 0) return true;
        return (
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        );
      })
      .map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        initials: getCustomerInitials(user.name),
      }));
  }, [primaryIds, exclusionIds, users, searchValue]);

  const handleSearchChange = useCallback((nextValue: string): void => {
    setSearchValue(nextValue);
  }, []);

  const handleRemove = useCallback(
    (personId: string): void => {
      dispatchSetField(dispatch, primaryKey, primaryIds.filter((id) => id !== personId));
    },
    [dispatch, dispatchSetField, primaryKey, primaryIds]
  );

  const handleAdd = useCallback(
    (personId: string): void => {
      dispatchSetField(dispatch, primaryKey, Array.from(new Set([...primaryIds, personId])));
    },
    [dispatch, dispatchSetField, primaryKey, primaryIds]
  );

  return {
    state: { selected, candidates, searchValue },
    handlers: { handleSearchChange, handleRemove, handleAdd },
  };
}
